// The scheduler itself: real Cal.com availability as day chips and a
// time grid, a functional confirm, and the booked confirmation. Times
// render in the viewer's own timezone. When availability won't load, it
// degrades to the phone number — the call is the product here, not the
// calendar. Shared by the modal (PhoneBand / nav) and the schedule-call
// station, so there is exactly one scheduler; each host supplies its own
// chrome around it.

import { useEffect, useMemo, useState, type ReactNode } from "react";
import { CheckIcon } from "@heroicons/react/24/outline";

import Button, { ButtonSpinner } from "components/Button";
import CorrectionRow from "components/CorrectionRow";
import { CONTINUE_BUTTON_CLASS } from "components/FormV2";
import { useFacebookPixel } from "components/FacebookPixelProvider";
import Skeleton from "components/Skeleton";
import { NetworkError } from "hooks/usePublicAPI";

import {
  fetchCallSlots,
  scheduleCall,
  type CallSlotDay,
  type ScheduledCall,
} from "./api";
import { useQuoteFlowAnalytics } from "./analytics";
import {
  CONFIRMATION_EMAIL_HIDDEN,
  bookingRequestBody,
  confirmationEmailCopy,
  emailEditError,
  emailEditorStart,
  emailStateAfterCorrection,
  emailBlocksBooking,
  emailStateAfterRefusal,
  type ConfirmationEmailCopy,
  type ConfirmationEmailState,
  type EmailCorrection,
} from "./booking-email";
import {
  bookingNotice,
  namedBookingFailure,
  type CallBookingFailure,
} from "./call-booking-failure";
import {
  bookedDayLabel,
  browserTimeZone,
  dayChipLabels,
  dayOnShow,
  preferredSlots,
  slotTimeLabel,
  timeZoneLongLabel,
  timeZoneShortLabel,
} from "./call-time";
import { SUPPORT_PHONE_HREF, SUPPORT_PHONE_LABEL } from "constants/phones";

/** The Facebook pixel's dedupe scope for a booking — one Schedule event
 * per quote per device, matching the pixel's own `_ate:{id}:{event}`
 * localStorage key. */
const FB_EVENT_DATA = {
  content_name: "pre-need-quote",
  content_category: "pre-need",
  lead_interest: "pre-need",
};

/** The mockup offers at most six day chips; further out, the call loses
 * to just calling. */
const MAX_DAYS = 6;

/** How many day chips the loading state holds space for — enough to
 * show a grid is coming without pulsing a whole calendar. */
const SKELETON_DAY_COUNT = 2;

/** A day chip's box with its two text lines pulsing — the exact
 * container the real chip renders, so the swap moves nothing. */
function DayChipSkeleton() {
  return (
    <div className="flex min-w-0 flex-col items-start rounded-[10px] border-[1.5px] border-oat-200 bg-white px-3.5 py-2.5">
      <span className="flex h-6 items-center">
        <Skeleton className="h-4 w-20" />
      </span>
      <span className="flex h-5 items-center">
        <Skeleton className="h-3 w-14" />
      </span>
    </div>
  );
}

/** The picker's one-line pitch — the modal shows it under its own
 * heading, the schedule-call station under the page title. */
export const SCHEDULER_BLURB =
  "Fifteen minutes with a licensed pre-planning agent. No obligation, and no pressure to decide on the call.";

export interface ScheduleCallBodyProps {
  quoteId: string;
  contactPhone: string | null;
  /** True while the arrival machine hasn't granted this device a session
   * yet — the calendar can't load without one. Rendering the scheduler
   * must never silently do nothing, so it shows the "one moment" state
   * instead of attempting (and failing) the slots fetch. */
  credentialPending?: boolean;
  /** A call already on the books — opens on its confirmation instead of
   * the picker. */
  existingCall?: ScheduledCall | null;
  /** The booked view's primary action — named for the slot it fills, not
   * where a given host sends it: the modal moves on to the application,
   * the schedule-call station to the quote screen. */
  onBookedContinue?: () => void;
  /** The host already titles the screen (the schedule-call station's
   * chrome does); the body then leaves its own headings out so the page
   * reads as one title, and only the state that changes underneath. */
  headless?: boolean;
  onBooked: (call: ScheduledCall) => void;
  /** Present = a "never mind" way out renders (the modal's dismiss); the
   * schedule-call station has no equivalent, so it leaves this unset and
   * relies on its own secondary "not ready" gate instead. */
  onClose?: () => void;
}

/** The arrival machine hasn't finished granting this device a session yet
 * (a bootstrap-priced quote still polling its handoff) — the calendar
 * needs that session to load slots, so asking for it now would just fail.
 * A distinct state, not the network-failure one: this isn't broken, it's
 * early. */
function PreparingState({ headless }: { headless: boolean }) {
  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-3">
        <ButtonSpinner />
        {!headless && (
          <h2 className="font-serif text-[31px] font-normal leading-tight text-soil">
            One moment
          </h2>
        )}
      </div>
      <p className="text-base leading-normal text-oat-700">
        We&rsquo;re still getting your quote ready. Hang on just a second and
        we&rsquo;ll bring up the calendar &mdash; or call us now at{" "}
        <a
          href={SUPPORT_PHONE_HREF}
          className="ph-no-capture font-bold text-soil"
        >
          {SUPPORT_PHONE_LABEL}
        </a>
        .
      </p>
    </div>
  );
}

function PhoneFallback({ headless }: { headless: boolean }) {
  return (
    <div className="flex flex-col gap-4">
      {!headless && (
        <h2 className="font-serif text-[31px] font-normal leading-tight text-soil">
          We couldn&rsquo;t load the calendar
        </h2>
      )}
      <p className="text-base leading-normal text-oat-700">
        Our scheduling calendar isn&rsquo;t answering right now. Call us instead
        &mdash; we&rsquo;re at{" "}
        <a
          href={SUPPORT_PHONE_HREF}
          className="ph-no-capture font-bold text-soil"
        >
          {SUPPORT_PHONE_LABEL}
        </a>
        , any time, day or night.
      </p>
    </div>
  );
}

function BookedView({
  call,
  contactPhone,
  timeZone,
  headless,
  onContinue,
  onClose,
}: {
  call: ScheduledCall;
  contactPhone: string | null;
  timeZone: string;
  headless: boolean;
  onContinue?: () => void;
  onClose?: () => void;
}) {
  const zone = timeZoneShortLabel(timeZone);
  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-3.5">
        <div className="flex h-[42px] w-[42px] flex-none items-center justify-center rounded-full bg-evergreen">
          <CheckIcon
            className="h-5 w-5 text-daisy"
            strokeWidth={3}
            aria-hidden="true"
          />
        </div>
        <div className="min-w-0 flex-1">
          {!headless && (
            <h2 className="font-serif text-[31px] leading-tight text-soil">
              Your call is set
            </h2>
          )}
          <p className="mt-0.5 text-base font-bold text-soil">
            {bookedDayLabel(call.start, timeZone)} at{" "}
            {slotTimeLabel(call.start, timeZone)}
            {zone ? ` ${zone}` : ""}
          </p>
        </div>
      </div>
      <p className="text-base leading-relaxed text-oat-700">
        A licensed pre-planning agent will call{" "}
        <span className="ph-no-capture">{contactPhone ?? "you"}</span>.
        There&rsquo;s nothing you need to do before then.
      </p>
      <div className="rounded-xl bg-oat px-5 py-4">
        <div className="text-[13px] font-bold uppercase tracking-[0.12em] text-oat-800">
          Want to save time on the call?
        </div>
        <p className="mt-2 text-base leading-normal text-soil">
          Submit your details now &mdash; legal name, a few health questions
          &mdash; and your application will be ready when we talk.
        </p>
        <p className="mt-3 text-base leading-normal text-soil">
          You can choose your plan and payment with the agent, or on your own
          before the call.
        </p>
      </div>
      {onContinue && (
        <button
          type="button"
          onClick={onContinue}
          className={`${CONTINUE_BUTTON_CLASS} w-full`}
        >
          Continue
        </button>
      )}
      {onClose && (
        <div className="border-t border-oat-200 pt-4">
          <button
            type="button"
            onClick={onClose}
            className="text-base font-bold text-oat-700 underline underline-offset-4 hover:text-plum"
          >
            I&rsquo;ll wait for the call
          </button>
        </div>
      )}
    </div>
  );
}

function confirmationStatement(copy: ConfirmationEmailCopy): ReactNode {
  return (
    <>
      {copy.lead}
      {copy.email !== null && (
        <b className="ph-no-capture break-words">{copy.email}</b>
      )}
      {copy.trail}
    </>
  );
}

export default function ScheduleCallBody({
  quoteId,
  contactPhone,
  credentialPending = false,
  existingCall,
  onBookedContinue,
  headless = false,
  onBooked,
  onClose,
}: ScheduleCallBodyProps) {
  const { trackEvent } = useFacebookPixel();
  const { track, reportFailure } = useQuoteFlowAnalytics();
  // Fixed for the component's life — remounting re-detects.
  const [timeZone] = useState(browserTimeZone);
  const [days, setDays] = useState<CallSlotDay[] | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [start, setStart] = useState<string | null>(null);
  const [booking, setBooking] = useState(false);
  const [bookFailure, setBookFailure] = useState<CallBookingFailure | null>(
    null,
  );
  // The email the family typed over the one on file. Held here and sent
  // with the booking; the server saves it to the contact only once Cal.com
  // has accepted it.
  const [emailCorrection, setEmailCorrection] = useState<EmailCorrection>(null);
  const [emailState, setEmailState] = useState<ConfirmationEmailState>(
    CONFIRMATION_EMAIL_HIDDEN,
  );
  const [editingEmail, setEditingEmail] = useState(false);
  const [booked, setBooked] = useState<ScheduledCall | null>(
    existingCall ?? null,
  );
  // The clock the slot grid lives against: slots retire the moment their
  // start passes, so someone who stepped away can't pick a time that is
  // already history. Ticks on an interval and whenever the tab comes
  // back into view — the step-away case.
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const tick = () => setNow(Date.now());
    const interval = setInterval(tick, 30_000);
    document.addEventListener("visibilitychange", tick);
    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", tick);
    };
  }, []);

  useEffect(() => {
    // Opened on an existing booking — there is nothing to pick. Opened
    // before the device has a session — the fetch would just 404; the
    // "one moment" state holds until credentialPending clears, and this
    // effect re-fires then.
    if (existingCall || credentialPending) return undefined;
    let cancelled = false;
    fetchCallSlots(quoteId, timeZone)
      .then((slots) => {
        if (!cancelled) setDays(slots.days);
      })
      .catch((error) => {
        if (!cancelled) {
          reportFailure("fetch_call_slots", error);
          setLoadFailed(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [quoteId, timeZone, existingCall, credentialPending, reportFailure]);

  // What is actually still bookable right now, thinned to a readable
  // handful. A slot stays offered until its start passes — a call in one
  // minute is still a call — and the elapsed ones drop out BEFORE the
  // thinning, so a passed 8:00 hands its half-hour to 8:10.
  const upcomingDays = useMemo(() => {
    if (days === null) return null;
    return days
      .map((day) => ({
        ...day,
        times: preferredSlots(
          day.times.filter((time) => Date.parse(time) > now),
          timeZone,
        ),
      }))
      .filter((day) => day.times.length > 0)
      .slice(0, MAX_DAYS);
  }, [days, now, timeZone]);

  const day =
    upcomingDays === null ? null : dayOnShow(upcomingDays, selectedDate);
  // The selection survives only while its slot does — a retired or
  // no-longer-offered slot reads as unchosen, never bookable.
  const chosenStart = day && start && day.times.includes(start) ? start : null;

  const confirm = async () => {
    if (!chosenStart) return;
    track("quote_flow_schedule_submitted", {});
    setBooking(true);
    setBookFailure(null);
    try {
      const { call } = await scheduleCall(
        quoteId,
        bookingRequestBody(chosenStart, timeZone, emailCorrection),
      );
      setBooked(call);
      onBooked(call);
      track("quote_flow_schedule_confirmed", {
        timezone: timeZone,
        hours_until_call: Math.round(
          (Date.parse(chosenStart) - Date.now()) / 3_600_000,
        ),
      });
      trackEvent({
        event: "Schedule",
        eventId: quoteId,
        data: FB_EVENT_DATA,
      });
    } catch (error) {
      // A 4xx is an expected refusal (a bad address, a closed quote) that
      // the family sees and can act on. A 5xx is a real booking failure
      // worth an engineer's eyes — including a slot another family took
      // first, which the API answers as a 502.
      const status = error instanceof NetworkError ? error.statusCode : null;
      if (!(error instanceof NetworkError) || error.statusCode >= 500) {
        reportFailure("schedule_call", error);
      }
      const failure =
        namedBookingFailure(error) ??
        (await recoverFromFailedBooking(chosenStart));
      setBookFailure(failure);
      if (failure.kind === "email_undeliverable") {
        const next = emailStateAfterRefusal(failure.email);
        setEmailState(next);
        setEditingEmail(confirmationEmailCopy(next)?.opensEditing ?? false);
      }
      track("quote_flow_schedule_failed", { reason: failure.kind, status });
    } finally {
      setBooking(false);
    }
  };

  const pickTime = (slotStart: string) => {
    setStart(slotStart);
    setBookFailure(null);
  };

  const applyEmail = (value: string) => {
    setEmailCorrection(value);
    setEmailState(emailStateAfterCorrection(value));
    setEditingEmail(false);
  };

  /**
   * The booking failed for a reason the API didn't name — most likely the
   * slot was taken (or passed) between serve and confirm. The fresh slots
   * tell the difference: if the failed time is gone from them, the slot was
   * the culprit and the family re-picks from the updated grid; if it is
   * still offered (or the refetch itself failed), the failure is generic.
   */
  const recoverFromFailedBooking = async (
    failedStart: string,
  ): Promise<CallBookingFailure> => {
    try {
      const slots = await fetchCallSlots(quoteId, timeZone);
      setDays(slots.days);
      const stillOffered = slots.days.some((day) =>
        day.times.includes(failedStart),
      );
      if (stillOffered) return { kind: "error" };
      setStart(null);
      return { kind: "slot_taken" };
    } catch (error) {
      reportFailure("fetch_call_slots", error);
      return { kind: "error" };
    }
  };

  const emailCopy = confirmationEmailCopy(emailState);
  const pickerNotice = bookingNotice(bookFailure);

  if (booked) {
    return (
      <BookedView
        call={booked}
        contactPhone={contactPhone}
        timeZone={timeZone}
        headless={headless}
        onContinue={onBookedContinue}
        onClose={onClose}
      />
    );
  }
  if (credentialPending) {
    return <PreparingState headless={headless} />;
  }
  if (loadFailed || (upcomingDays !== null && upcomingDays.length === 0)) {
    return <PhoneFallback headless={headless} />;
  }

  // The picker's chrome renders before the slots arrive — title, blurb,
  // day-grid space and footer all stand from the first paint, with a
  // couple of skeleton chips marking where the days go; the real chips
  // may extend the grid by a row when they land.
  return (
    <div>
      {!headless && (
        <>
          <h2 className="font-serif text-[31px] font-normal leading-tight text-soil">
            Pick a time that{" "}
            <span className="font-sans font-bold text-evergreen">
              works for you
            </span>
          </h2>
          <p className="mt-2.5 text-base leading-normal text-oat-700">
            {SCHEDULER_BLURB}
          </p>
        </>
      )}

      <div className={headless ? "" : "mt-6"}>
        <div className="mb-2.5 text-[13px] font-bold uppercase tracking-[0.12em] text-oat-800">
          What day works?
        </div>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
          {upcomingDays === null
            ? Array.from({ length: SKELETON_DAY_COUNT }, (_, slot) => (
                <DayChipSkeleton key={slot} />
              ))
            : null}
          {(upcomingDays ?? []).map((slotDay) => {
            const chip = dayChipLabels(slotDay.date, timeZone);
            const selected = slotDay.date === day?.date;
            return (
              <button
                key={slotDay.date}
                type="button"
                onClick={() => {
                  setSelectedDate(slotDay.date);
                  setStart(null);
                  setBookFailure(null);
                }}
                className={`flex min-w-0 flex-col items-start rounded-[10px] border-[1.5px] px-3.5 py-2.5 text-left transition-all ${
                  selected
                    ? "border-soil bg-soil text-ivory"
                    : "border-oat-200 bg-white text-soil hover:border-evergreen/50"
                }`}
              >
                <span className="text-base font-bold">{chip.label}</span>
                <span className="text-[13px] opacity-75">{chip.sub}</span>
              </button>
            );
          })}
        </div>
      </div>

      {day ? (
        <div className="mt-5">
          {/* On a phone the heading stands alone and the zone and length
              share the line beneath it; from `sm` up the zone joins the
              heading and the length sits at the right edge. */}
          <div className="mb-2.5 flex flex-col gap-1 sm:flex-row sm:items-baseline sm:justify-between sm:gap-3">
            <div className="text-[13px] font-bold uppercase tracking-[0.12em] text-oat-800">
              What time?
              <span className="hidden font-normal normal-case tracking-normal sm:inline">
                {" "}
                {timeZoneLongLabel(timeZone)}
              </span>
            </div>
            <div className="text-[13px] text-oat-700">
              <span className="sm:hidden">
                {timeZoneLongLabel(timeZone)} ·{" "}
              </span>
              {dayChipLabels(day.date, timeZone).label === "Today"
                ? "Today’s remaining slots"
                : "15-minute call"}
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
            {day.times.map((slotStart) => {
              const selected = slotStart === chosenStart;
              return (
                <button
                  key={slotStart}
                  type="button"
                  onClick={() => pickTime(slotStart)}
                  className={`flex h-[46px] items-center justify-center whitespace-nowrap rounded-lg border-[1.5px] px-1 text-[15px] font-bold tabular-nums transition-all sm:text-base ${
                    selected
                      ? "border-soil bg-soil text-ivory"
                      : "border-oat-200 bg-white text-soil hover:border-evergreen/50"
                  }`}
                >
                  {slotTimeLabel(slotStart, timeZone)}
                </button>
              );
            })}
          </div>
        </div>
      ) : null}

      {emailCopy && (
        <div className="mt-5 text-base leading-normal text-soil">
          <CorrectionRow
            statement={confirmationStatement(emailCopy)}
            tone={emailCopy.tone}
            initialValueRefused={emailState.kind === "refused"}
            prompt={emailCopy.prompt}
            fieldLabel="Email address"
            inputType="email"
            autoComplete="email"
            editing={editingEmail}
            onEdit={() => setEditingEmail(true)}
            onCancel={() => setEditingEmail(false)}
            initialValue={emailEditorStart(emailState)}
            validate={emailEditError}
            onApply={applyEmail}
          />
        </div>
      )}

      {pickerNotice ? (
        <p role="alert" className="mt-4 text-sm text-red-800">
          {pickerNotice}
        </p>
      ) : null}

      <div className="mt-6 flex flex-col items-stretch gap-4 sm:flex-row sm:items-center">
        <Button
          variant="continue"
          className="w-full sm:w-auto"
          disabled={
            !chosenStart || editingEmail || emailBlocksBooking(emailState)
          }
          isLoading={booking}
          loadingText="Booking…"
          onClick={confirm}
        >
          Book my call
        </Button>
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            className="self-center text-base font-bold text-evergreen underline underline-offset-4 hover:text-plum"
          >
            Never mind
          </button>
        )}
      </div>
      <p className="mt-5 text-base leading-normal text-oat-700">
        Prefer to call us? We&rsquo;re at{" "}
        <a
          href={SUPPORT_PHONE_HREF}
          className="ph-no-capture font-bold text-soil"
        >
          {SUPPORT_PHONE_LABEL}
        </a>
        , any time, day or night.
      </p>
    </div>
  );
}
